// src/utils/videoFeedback.js
//
// Pass 73 — the ONE canonical read/write model for timestamped video feedback. The stream lives at
// /videos/{videoId}/timestampedFeedback/{feedbackId} (unchanged — no second feedback store). This replaces the
// raw-component addDoc (a fresh id per retry) with a stable-id, idempotent, author-stamped writer, plus one shared
// reader/normalizer so the instructor editor and the student review page can never disagree.

import { runTransaction, doc, collection, onSnapshot, query, orderBy, deleteDoc, serverTimestamp } from 'firebase/firestore';

const str = (v) => (v == null ? '' : String(v).trim());
const fail = (code, msg) => { const e = new Error(msg); e.code = code; return e; };
const num = (v) => (Number.isFinite(+v) ? +v : NaN);

export const FEEDBACK_CONTENT_REQUIRED = 'Add a comment before saving feedback.';
export const FEEDBACK_CONFLICT = 'This feedback note already exists with different content.';
export const MAX_FEEDBACK_CONTENT = 4000;

// A timestamped-feedback item's canonical time is INTEGER seconds from the start of the video, clamped to >= 0.
export function normalizeTimestampedFeedback(docSnapOrData, fallbackId = '') {
  const hasSnap = typeof docSnapOrData?.data === 'function';
  const raw = hasSnap ? (docSnapOrData.data() || {}) : (docSnapOrData || {});
  const id = hasSnap ? docSnapOrData.id : (raw.id || fallbackId || '');
  const t = num(raw.timestamp);
  return {
    id,
    videoId: str(raw.videoId),
    timestamp: Number.isFinite(t) ? Math.max(0, Math.floor(t)) : 0, // §22 — malformed legacy time clamps, never crashes
    hasValidTimestamp: Number.isFinite(t) && t >= 0,
    type: str(raw.type) || 'text',
    content: str(raw.content),
    createdByAuthUserId: str(raw.createdByAuthUserId), // '' for legacy items → UI shows a neutral "Coach"
    createdAt: raw.createdAt || null,
  };
}

// §21 — deterministic order: timestamp asc, then createdAt (seconds), then id. Never relies on snapshot order.
function sortFeedback(list) {
  return [...list].sort((a, b) => {
    if (a.timestamp !== b.timestamp) return a.timestamp - b.timestamp;
    const ac = a.createdAt?.seconds || 0; const bc = b.createdAt?.seconds || 0;
    if (ac !== bc) return ac - bc;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

// subscribeTimestampedFeedback(db, videoId, onData, onError) → unsubscribe. The ONE realtime reader shared by the
// instructor editor and the student page (independent of the parent-video subscription, so a new comment never
// refetches the whole video).
export function subscribeTimestampedFeedback(db, videoId, onData, onError = () => {}) {
  const id = str(videoId);
  if (!id) { onData([]); return () => {}; }
  const q = query(collection(db, 'videos', id, 'timestampedFeedback'), orderBy('timestamp', 'asc'));
  return onSnapshot(
    q,
    (snap) => onData(sortFeedback(snap.docs.map((d) => normalizeTimestampedFeedback(d)))),
    (err) => onError(err),
  );
}

// createTimestampedFeedback(db, { videoId, feedbackId, timestamp, content, type, actorAuthUserId })
// The caller preallocates feedbackId ONCE per draft (doc(collection(...)).id) so a retry / double-click lands on
// the SAME child — idempotent when the payload matches, a visible conflict when it differs (never a silent mutate).
// Author is stamped durably as the reviewer's Auth uid (§11) — never inferred later from the current assignment.
export async function createTimestampedFeedback(db, { videoId = '', feedbackId = '', timestamp = 0, content = '', type = 'text', actorAuthUserId = '' } = {}) {
  const vid = str(videoId);
  const fid = str(feedbackId);
  const uid = str(actorAuthUserId);
  const body = str(content).slice(0, MAX_FEEDBACK_CONTENT);
  const t = num(timestamp);
  if (!vid || !fid) throw fail('FEEDBACK_ID_REQUIRED', 'A video id and feedback id are required.');
  if (!uid) throw fail('FEEDBACK_ACTOR_REQUIRED', 'You must be signed in to add feedback.');
  if (!body) throw fail('FEEDBACK_CONTENT_REQUIRED', FEEDBACK_CONTENT_REQUIRED);
  const time = Number.isFinite(t) ? Math.max(0, Math.floor(t)) : 0;

  let created = false;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'videos', vid, 'timestampedFeedback', fid);
    const snap = await tx.get(ref);
    if (snap.exists && snap.exists()) {
      const cur = snap.data() || {};
      // idempotent when the same author wrote the same note at the same time; a different material payload conflicts.
      if (num(cur.timestamp) === time && str(cur.content) === body && str(cur.createdByAuthUserId) === uid) return;
      throw fail('FEEDBACK_CONFLICT', FEEDBACK_CONFLICT);
    }
    tx.set(ref, {
      videoId: vid,
      timestamp: time,
      type: 'text', // §7 — the mounted writer only ever creates text; no fake configurability
      content: body,
      createdByAuthUserId: uid,
      createdAt: serverTimestamp(),
    });
    created = true;
  });
  return { videoId: vid, feedbackId: fid, created };
}

export async function deleteTimestampedFeedback(db, { videoId = '', feedbackId = '' } = {}) {
  const vid = str(videoId); const fid = str(feedbackId);
  if (!vid || !fid) throw fail('FEEDBACK_ID_REQUIRED', 'A video id and feedback id are required.');
  await deleteDoc(doc(db, 'videos', vid, 'timestampedFeedback', fid)); // Rules enforce: assignee + parent pending
  return { videoId: vid, feedbackId: fid, deleted: true };
}
